import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { fetchJobStatus } from "../helpers/eirna_backend_helper"; 

// Default formatter just returns the raw data
const defaultFormatter = (data) => data;


export const useBatchPolling = (formatResult = defaultFormatter) => {
  const navigate = useNavigate();
  
  const [batchCode, setBatchCode] = useState("");
  const [results, setResults] = useState([]);
  const [activeTasks, setActiveTasks] = useState([]);
  const [batchLoading, setBatchLoading] = useState(false);

  const pollTaskStatus = useCallback(async (taskId) => {
    try {
      const result = await fetchJobStatus(taskId);

      if (["SUCCESS", "FAILURE"].includes(result.state)) {
        // Remove from active tasks list
        setActiveTasks((prev) => prev.filter((id) => id !== taskId));

        if (result.state === "SUCCESS") {
          if (result.result.message === "user logged out") {
            navigate("/login");
            return;
          }
          
          // Apply the formatting function passed into the hook
          const formattedResult = formatResult(result.result.summary_dict);
          setResults((prev) => [...prev, formattedResult]);
        } else {
          toast.error(`Job ${taskId} failed. Please contact support.`);
        }
      } else {
        // Task still running, poll again
        setTimeout(() => pollTaskStatus(taskId), 2000);
      }
    } catch (err) {
      console.error(err);
      setActiveTasks((prev) => prev.filter((id) => id !== taskId));
    }
  }, [navigate, formatResult]);

  // Stop global batch loading if all tasks complete
  useEffect(() => {
    if (batchLoading && activeTasks.length === 0) {
      setBatchLoading(false);
      toast.success("All batch jobs have completed.");
    }
  }, [activeTasks, batchLoading]);

  // A handy helper to reset states when a new batch starts
  const initNewBatch = (newBatchCode) => {
    setBatchCode(newBatchCode);
    setResults([]);
    setActiveTasks([]);
    setBatchLoading(true);
  };

  const copyBatchURLToClipboard = () => {
    console.log("copyBatchURLToClipboard called")
    const jobURL = `${window.location.origin}/short/${batchCode}`;
    
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(jobURL)
        .then(() => toast.success("Batch URL copied to clipboard!"))
        .catch((err) => console.error("Failed to copy: ", err));
    } else {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = jobURL;
      textArea.style.position = "absolute";
      textArea.style.left = "-999999px";
      document.body.prepend(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        toast.success("Batch URL copied to clipboard!");
      } catch (error) {
        console.error(error);
      } finally {
        textArea.remove();
      }
    }
  };

  return {
    batchCode,
    setBatchCode,
    results,
    setResults,
    activeTasks,
    setActiveTasks,
    batchLoading,
    setBatchLoading,
    pollTaskStatus,
    initNewBatch,
    copyBatchURLToClipboard
  };
};